import { resolveWorkspaceAssetUrl } from '../../lib/urls';
/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Widget Embed Code Generator & Setup Guide Component (Task 10.3.6)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import React, { useState } from 'react';
import { useWidgetStudioStore } from '../../stores/useWidgetStudioStore';
import { useI18n } from '../../hooks/useI18n';
import { Code2, Copy, Check, ExternalLink, Terminal, Globe, Layers } from 'lucide-react';

export const WidgetEmbedCodeTab: React.FC = () => {
  const { t } = useI18n();
  const { config } = useWidgetStudioStore();
  const [copied, setCopied] = useState(false);
  const [guidePlatform, setGuidePlatform] = useState<'html' | 'wordpress' | 'nextjs' | 'gtm'>('html');

  const embedScriptTag = `<!-- Sophia AI Chatbot Widget (Zero CSS Leakage Shadow DOM) -->
<script
  src="${resolveWorkspaceAssetUrl('widget.js')}"
  data-bot-id="${config.botId}"
  data-theme-color="${config.appearance.primaryColor}"
  data-position="${config.appearance.position}"
  defer
></script>`;

  const handleCopyCode = () => {
    navigator.clipboard.writeText(embedScriptTag);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="space-y-6">
      {/* 1. Embed Code Snippet Box */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-heading font-bold text-foreground flex items-center gap-2">
            <Code2 className="w-4 h-4 text-primary" />
            <span>{t('widget.embedScriptHeading')}</span>
          </h3>
          <span className="text-[11px] text-emerald-500 font-mono font-bold bg-emerald-500/10 px-2 py-0.5 rounded-full border border-emerald-500/20">
            {t('widget.embedScriptSize')}
          </span>
        </div>
        <p className="text-xs text-foreground-muted">
          {t('widget.embedScriptDesc')}
        </p>

        <div className="relative rounded-2xl border border-surface-border bg-surface-elevated p-4 font-mono text-xs text-foreground overflow-x-auto shadow-inner">
          <button
            type="button"
            onClick={handleCopyCode}
            className="absolute top-3 right-3 px-3 py-1.5 rounded-xl border border-surface-border bg-surface text-foreground hover:bg-muted text-xs font-sans font-semibold flex items-center gap-1.5 transition-colors shadow-sm"
          >
            {copied ? (
              <>
                <Check className="w-3.5 h-3.5 text-emerald-400" />
                <span className="text-emerald-400">{t('widget.copiedEmbedBtn')}</span>
              </>
            ) : (
              <>
                <Copy className="w-3.5 h-3.5" />
                <span>{t('widget.copyEmbedBtn')}</span>
              </>
            )}
          </button>

          <pre className="pr-28 text-[11px] leading-relaxed">
            <code>{embedScriptTag}</code>
          </pre>
        </div>
      </div>

      <hr className="border-surface-border" />

      {/* 2. Platform Integration Guides */}
      <div className="space-y-4">
        <h4 className="text-xs font-heading font-bold text-foreground uppercase tracking-wider flex items-center gap-2">
          <Terminal className="w-4 h-4 text-primary" />
          <span>{t('widget.guideHeading')}</span>
        </h4>

        {/* Platform Tabs */}
        <div className="flex border-b border-surface-border gap-2">
          {[
            { id: 'html', label: t('widget.guideHtml'), icon: Globe },
            { id: 'wordpress', label: t('widget.guideWp'), icon: Layers },
            { id: 'nextjs', label: t('widget.guideNextjs'), icon: Code2 },
            { id: 'gtm', label: t('widget.guideGtm'), icon: ExternalLink },
          ].map((tab) => {
            const isSelected = guidePlatform === tab.id;
            const TabIcon = tab.icon;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => setGuidePlatform(tab.id as typeof guidePlatform)}
                className={`flex items-center gap-1.5 px-3 py-2 text-xs font-medium border-b-2 -mb-px transition-colors ${
                  isSelected
                    ? 'border-primary text-primary font-bold'
                    : 'border-transparent text-foreground-muted hover:text-foreground'
                }`}
              >
                <TabIcon className="w-3.5 h-3.5" />
                <span>{tab.label}</span>
              </button>
            );
          })}
        </div>

        {/* Guide Content */}
        <div className="p-4 rounded-xl border border-surface-border bg-surface text-xs leading-relaxed space-y-3">
          {guidePlatform === 'html' && (
            <div className="space-y-2">
              <p className="font-semibold text-foreground">{t('widget.guideHtmlDesc')}</p>
              <ol className="list-decimal list-inside space-y-1 text-foreground-muted">
                <li>{t('widget.guideHtmlStep1')}</li>
                <li>{t('widget.guideHtmlStep2')}</li>
                <li>{t('widget.guideHtmlStep3')}</li>
              </ol>
            </div>
          )}

          {guidePlatform === 'wordpress' && (
            <div className="space-y-2">
              <p className="font-semibold text-foreground">{t('widget.guideWpDesc')}</p>
              <ol className="list-decimal list-inside space-y-1 text-foreground-muted">
                <li>{t('widget.guideHtmlStep1')} (<code>header.php</code> / <code>footer.php</code>).</li>
                <li>{t('widget.guideHtmlStep2')}</li>
                <li>{t('widget.guideHtmlStep3')}</li>
              </ol>
            </div>
          )}

          {guidePlatform === 'nextjs' && (
            <div className="space-y-2">
              <p className="font-semibold text-foreground">{t('widget.guideNextjsDesc')}</p>
              <pre className="p-3 rounded-lg bg-surface-elevated font-mono text-[11px] text-foreground overflow-x-auto">
{`import Script from 'next/script';

export default function RootLayout({ children }) {
  return (
    <html lang="en">
      <body>
        {children}
        <Script
          src="${resolveWorkspaceAssetUrl('widget.js')}"
          data-bot-id="${config.botId}"
          data-theme-color="${config.appearance.primaryColor}"
          strategy="lazyOnload"
        />
      </body>
    </html>
  );
}`}
              </pre>
            </div>
          )}

          {guidePlatform === 'gtm' && (
            <div className="space-y-2">
              <p className="font-semibold text-foreground">{t('widget.guideGtmDesc')}</p>
              <ol className="list-decimal list-inside space-y-1 text-foreground-muted">
                <li>Google Tag Manager $\to$ Tags $\to$ New Tag.</li>
                <li>Tag Configuration: <strong>Custom HTML</strong>, paste the script above.</li>
                <li>Triggering: <strong>All Pages</strong> or <strong>Initialization - All Pages</strong>.</li>
                <li>Submit & Publish container.</li>
              </ol>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
